import api from '../api';

/**
 * Shared Pi wallet top-up flow
 *
 * Reusable from:
 * - MyWallet manual drawer
 * - future quick top-up popup
 * - any other "not enough Pi" flow
 *
 * This helper does NOT touch React state directly.
 * It only returns structured results and uses callbacks.
 */

function getHostNetworkInfo() {
  const host = window.location.hostname || '';
  const isTestnetHost = host.includes('testnet.');
  const isSandbox = isTestnetHost;
  const network = isTestnetHost ? 'testnet' : 'mainnet';

  return {
    host,
    isTestnetHost,
    isSandbox,
    network,
  };
}

function getMinTopUpPi() {
  return Number(process.env.REACT_APP_MIN_TOPUP_PI || 0.1);
}

function getAppIds() {
  return {
    testnet: process.env.REACT_APP_PI_APP_ID_TESTNET,
    mainnet: process.env.REACT_APP_PI_APP_ID_MAINNET,
  };
}

async function loadPiSdkIfNeeded() {
  if (window.Pi && window.Pi.init) return true;

  await new Promise((resolve) => {
    const existing = document.querySelector('script[data-pi-sdk="true"]');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', resolve, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://sdk.minepi.com/pi-sdk.js';
    script.async = true;
    script.dataset.piSdk = 'true';
    script.onload = resolve;
    script.onerror = resolve;
    document.head.appendChild(script);
  });

  return !!(window.Pi && window.Pi.init);
}

/**
 * Safe Pi SDK init.
 * You already init in MyWallet, but this keeps the util reusable
 * in other screens/popups too.
 */
async function ensurePiSdkInitialized() {
  const available = await loadPiSdkIfNeeded();
  if (!available) {
    return {
      ok: false,
      code: 'PI_SDK_MISSING',
      message: 'Pi SDK not available.',
    };
  }

  const { isSandbox, network } = getHostNetworkInfo();
  const appIds = getAppIds();
  const appId = isSandbox ? appIds.testnet : appIds.mainnet;

  try {
    window.Pi.init({
      version: '2.0',
      sandbox: isSandbox,
      appId,
    });

    return {
      ok: true,
      network,
      isSandbox,
    };
  } catch (err) {
    console.error('❌ Failed to initialize Pi SDK in util:', err);
    return {
      ok: false,
      code: 'PI_INIT_FAILED',
      message: err?.message || 'Failed to initialize Pi SDK.',
    };
  }
}

async function ensurePiPaymentsAuth(onIncompletePaymentFound) {
  const Pi = window.Pi;

  // Allow local/dev without Pi Browser hard fail
  if (!Pi || !Pi.authenticate) {
    return { ok: true };
  }

  try {
    const auth = await Pi.authenticate(
      ['username', 'payments'],
      onIncompletePaymentFound
    );
    console.log('✅ Pi authenticate OK:', auth);
    return { ok: true };
  } catch (err) {
    console.error('❌ Pi authenticate failed:', err);

    try {
      const retry = await Pi.authenticate(
        ['payments'],
        onIncompletePaymentFound
      );
      console.log('✅ Pi authenticate retry OK:', retry);
      return { ok: true };
    } catch (retryErr) {
      return {
        ok: false,
        code: 'PI_AUTH_FAILED',
        message: retryErr?.message || 'Pi authentication failed.',
      };
    }
  }
}

/**
 * startPiTopUp
 *
 * @param {Object} options
 * @param {number|string} options.amount
 * @param {string} options.username
 * @param {Function} options.t - i18next t function
 * @param {Function} [options.onWalletRefresh] - optional callback after success/recovery
 * @param {Function} [options.onSuccess] - optional callback after success
 * @param {Function} [options.onError] - optional callback for surfaced errors
 * @param {Function} [options.onCancel] - optional callback when user cancels
 * @param {Function} [options.onRecovered] - optional callback when incomplete payment is recovered
 *
 * @returns {Promise<{ok:boolean, code?:string, message?:string, cancelled?:boolean, result?:any}>}
 */
export async function startPiTopUp({
  amount,
  username,
  t,
  onWalletRefresh,
  onSuccess,
  onError,
  onCancel,
  onRecovered,
}) {
  const numericAmount = Number(amount);
  const minTopUp = getMinTopUpPi();

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    const result = {
      ok: false,
      code: 'INVALID_AMOUNT',
      message: t('topup.errors.invalid_amount'),
    };
    if (onError) onError(result);
    return result;
  }

  if (numericAmount < minTopUp) {
    const result = {
      ok: false,
      code: 'MIN_TOPUP_AMOUNT',
      message: t('topup.errors.min', { min: minTopUp }),
    };
    if (onError) onError(result);
    return result;
  }

  if (!username) {
    const result = {
      ok: false,
      code: 'MISSING_USERNAME',
      message: 'Missing username.',
    };
    if (onError) onError(result);
    return result;
  }

  const sdkReady = await ensurePiSdkInitialized();
  if (!sdkReady.ok) {
    const result = {
      ok: false,
      code: sdkReady.code || 'PI_SDK_MISSING',
      message: t('topup.errors.pi_sdk_missing'),
    };
    if (onError) onError(result);
    return result;
  }

  const Pi = window.Pi;
  if (!Pi || !Pi.createPayment) {
    const result = {
      ok: false,
      code: 'PI_CREATE_PAYMENT_MISSING',
      message: t('topup.errors.pi_sdk_missing'),
    };
    if (onError) onError(result);
    return result;
  }

  const { network } = getHostNetworkInfo();
  const accessToken = localStorage.getItem('token');

  const config = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-Network': network,
    },
  };

  const safeRefreshWallet = async () => {
    if (typeof onWalletRefresh === 'function') {
      await onWalletRefresh();
    }
  };

  const handleIncompletePaymentFound = async (payment) => {
    console.warn('⚠️ Incomplete payment found:', payment);

    try {
      await api.post(
        '/payments/approve',
        { paymentId: payment.identifier },
        config
      );

      await api.post(
        '/payments/complete',
        {
          paymentId: payment.identifier,
          txid: payment.transaction?.txid,
        },
        config
      );

      await safeRefreshWallet();

      const recoveredResult = {
        ok: true,
        code: 'PAYMENT_RECOVERED',
        message: t('topup.recovered'),
        result: {
          paymentId: payment.identifier,
          txid: payment.transaction?.txid || null,
          network,
        },
      };

      if (onRecovered) onRecovered(recoveredResult);
      if (onSuccess) onSuccess(recoveredResult);

      return recoveredResult;
    } catch (err) {
      console.error(
        '❌ Failed to recover incomplete payment:',
        err?.response?.data || err.message
      );

      const failResult = {
        ok: false,
        code: 'INCOMPLETE_PAYMENT_RECOVERY_FAILED',
        message:
          err?.response?.data?.error ||
          err?.message ||
          'Failed to recover incomplete payment.',
      };

      if (onError) onError(failResult);
      return failResult;
    }
  };

  const authed = await ensurePiPaymentsAuth(handleIncompletePaymentFound);
  if (!authed.ok) {
    const result = {
      ok: false,
      code: authed.code || 'PI_AUTH_FAILED',
      message: authed.message || 'Pi authentication failed.',
    };
    if (onError) onError(result);
    return result;
  }

  const paymentData = {
    amount: numericAmount,
    memo: t('topup.memo', { amount: numericAmount }),
    metadata: {
      username,
      type: 'wallet_topup',
    },
  };

  return new Promise((resolve) => {
    let settled = false;

    const finish = (payload) => {
      if (settled) return;
      settled = true;
      resolve(payload);
    };

    const onReadyForServerApproval = async (paymentId) => {
      try {
        await api.post('/payments/approve', { paymentId }, config);
        console.log('✅ Payment approved on backend');
      } catch (err) {
        console.error(
          '❌ Payment approval failed:',
          err?.response?.data || err.message
        );

        const result = {
          ok: false,
          code: 'PAYMENT_APPROVAL_FAILED',
          message: t('topup.errors.approval_failed', {
            err: err?.response?.data?.error || err.message,
          }),
        };

        if (onError) onError(result);
        finish(result);
      }
    };

    const onReadyForServerCompletion = async (paymentId, txid) => {
      try {
        const res = await api.post(
          '/payments/complete',
          { paymentId, txid },
          config
        );

        console.log('✅ Payment complete response:', res.data);

        await safeRefreshWallet();

        const result = {
          ok: true,
          code: 'TOPUP_SUCCESS',
          message: t('topup.success', { amount: numericAmount }),
          result: {
            paymentId,
            txid,
            network,
            response: res.data,
          },
        };

        if (onSuccess) onSuccess(result);
        finish(result);
      } catch (err) {
        console.error(
          '❌ Payment completion failed:',
          err?.response?.data || err.message
        );

        const result = {
          ok: false,
          code: 'PAYMENT_COMPLETION_FAILED',
          message: t('topup.errors.completion_failed', {
            err: err?.response?.data?.error || err.message,
          }),
        };

        if (onError) onError(result);
        finish(result);
      }
    };

    const onPiCancel = (paymentId) => {
      console.warn('❌ Payment cancelled by user:', paymentId);

      const result = {
        ok: false,
        cancelled: true,
        code: 'PAYMENT_CANCELLED',
        message: t('topup.errors.cancelled'),
        result: { paymentId },
      };

      if (onCancel) onCancel(result);
      finish(result);
    };

    const onPiError = (err) => {
      console.error('❌ Pi payment error:', err);

      const msg =
        typeof err === 'string' ? err : err?.message || 'Unknown error';

      const result = {
        ok: false,
        code: 'PI_PAYMENT_ERROR',
        message: t('topup.errors.generic', { msg }),
      };

      if (onError) onError(result);
      finish(result);
    };

    try {
      Pi.createPayment(paymentData, {
        onReadyForServerApproval,
        onReadyForServerCompletion,
        onIncompletePaymentFound: handleIncompletePaymentFound,
        onCancel: onPiCancel,
        onError: onPiError,
      });
    } catch (err) {
      console.error('❌ Pi createPayment error:', err);

      const result = {
        ok: false,
        code: 'PI_CREATE_PAYMENT_FAILED',
        message: t('topup.errors.start_failed', {
          msg: err?.message || String(err),
        }),
      };

      if (onError) onError(result);
      finish(result);
    }
  });
}
